import { useEffect, useState } from 'react';
import { ApiError } from '@/api/client';
import { effectivePerms, summarizeRole } from './permissions';
import { createRoleFromTemplate } from './api';
import type { Role } from './types';
import s from './RoleCreateSheet.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

export function RoleCreateSheet({ roles, initialTemplateId, canCreate, onClose, onCreated }: {
  roles: Role[];
  initialTemplateId?: string;
  canCreate: boolean;
  onClose: () => void;
  onCreated: (role: Role) => void;
}) {
  const templates = roles.filter((role) => role.isActive);
  const [templateId, setTemplateId] = useState(() => templates.find((role) => role.id === initialTemplateId)?.id || templates.find((role) => role.code === 'WAREHOUSE')?.id || templates[0]?.id || '');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!templates.length) return;
    setTemplateId((current) => templates.some((role) => role.id === current)
      ? current
      : templates.find((role) => role.id === initialTemplateId)?.id || templates.find((role) => role.code === 'WAREHOUSE')?.id || templates[0].id);
  }, [roles, initialTemplateId]);
  const template = templates.find((role) => role.id === templateId);
  const templatePermissionCount = template ? effectivePerms(template).size : 0;
  const templateSummary = template ? summarizeRole(template) : null;
  const valid = name.trim().length > 0 && /^[A-Z][A-Z0-9_]{2,31}$/.test(code) && !!template;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);
  const submit = async () => {
    if (!valid || !template || !canCreate || READONLY || saving || uncertain) return;
    setSaving(true); setError('');
    try {
      const created = await createRoleFromTemplate({
        code, name: name.trim(), description: description.trim(), baseRole: template.baseRole,
        permissions: [...template.permissions], restrictions: [...template.restrictions], warehouseIds: template.warehouseIds,
      });
      onCreated(created);
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '创建角色失败');
    } finally { setSaving(false); }
  };
  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className={s.sheet} role="dialog" aria-modal="true" aria-label="新建角色">
      <header className={s.head}><button className={s.cancelTop} type="button" onClick={onClose} disabled={saving}>取消</button><b>新建角色</b><button type="button" onClick={onClose} disabled={saving} aria-label="关闭">×</button></header>
      <div className={s.body}>
        <div className={s.grid}>
          <label><span><i>*</i> 角色名称</span><input autoFocus value={name} maxLength={80} placeholder="例如：仓库主管" onChange={(event) => setName(event.target.value)} /></label>
          <label><span><i>*</i> 角色编码</span><input className="tnum" value={code} maxLength={32} placeholder="WAREHOUSE_LEAD" onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} /><small>创建后不可修改</small></label>
          <label className={s.wide}><span>说明</span><input value={description} maxLength={300} placeholder="这个角色负责什么" onChange={(event) => setDescription(event.target.value)} /></label>
          <label className={s.wide}><span><i>*</i> 以哪个角色为模板</span><select value={templateId} onChange={(event) => setTemplateId(event.target.value)}>{templates.map((role) => <option key={role.id} value={role.id}>{role.name}（{role.isPreset ? '预置' : '自定义'}）· {effectivePerms(role).size} 项权限</option>)}</select></label>
        </div>
        {template && templateSummary && <section className={s.templatePreview} aria-label={`${template.name}模板摘要`}>
          <b>「{template.name}」现在能干什么</b>
          <p className={s.previewCan}>{templateSummary.headline}</p>
          <p className={s.previewCannot}>{templateSummary.hidden.length ? `看不到${templateSummary.hidden.join('和')}。` : '当前角色没有隐藏的敏感字段。'}</p>
        </section>}
        {template && <div className={s.templateNote}><span aria-hidden="true">⚠</span><div><b>模板只是一键填充，填完就脱钩</b><p>新角色会复制「{template.name}」当前的 {templatePermissionCount} 项权限作为初始值。<b>之后修改「{template.name}」不会影响这个新角色</b>，反之亦然。</p></div></div>}
        {!templates.length && <p className={s.error}>没有可用的启用角色模板。</p>}
        {READONLY && <p className={s.note}>当前为生产只读模式，可以查看窗口，暂不能提交。</p>}
        {!canCreate && <p className={s.note}>当前账号没有新建角色权限。</p>}
        {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请先核对角色列表，暂勿重复提交。'}</p>}
      </div>
      <footer className={s.foot}><button type="button" onClick={onClose} disabled={saving}>取消</button><button type="button" className={s.primary} disabled={!valid || !canCreate || READONLY || saving || uncertain} onClick={() => void submit()}>{saving ? '创建中…' : '创建并编辑权限'}</button></footer>
    </section>
  </div>;
}
